import { Chess } from 'chess.js';
import { positionContext } from './position-identity.js';
import { trackPositionRendering } from './position-render-state.js';
import { LAST_MOVE_MARKER } from './board-markers.js';

/**
 * Returns true when the FEN's castling field contains file-based Chess960
 * rights (any character other than K, Q, k, q, or -).
 * e.g. "GAga" in "rkbnnbrq/pppppppp/8/8/8/8/PPPPPPPP/RKBNNBRQ w GAga - 0 1"
 */
function isChess960Fen(fen) {
    if (!fen) return false;
    const castling = fen.trim().split(/\s+/)[2] || '-';
    return [...castling].some(c => !'KQkq-'.includes(c));
}

function buildMoveMetadata(startFen, moves) {
    const metadata = {
        lastMoveSquares: [null],
        moveSounds: [null]
    };

    if (!startFen || !Array.isArray(moves) || moves.length === 0) {
        return metadata;
    }

    // chess.js 1.3 does not implement Chess960 file-letter castling rights;
    // its second constructor argument only supports skipValidation. The
    // server-provided FEN list is authoritative, so omit optional metadata
    // reconstruction instead of letting validation abort visible replay.
    if (isChess960Fen(startFen)) {
        return metadata;
    }

    let chess;
    try {
        chess = new Chess(startFen);
    } catch (error) {
        console.warn('FelixChessReplayController: move metadata unavailable for start FEN');
        return metadata;
    }
    for (const san of moves) {
        const result = chess.move(san, { sloppy: true });
        if (!result) {
            break;
        }
        metadata.lastMoveSquares.push({ from: result.from, to: result.to });
        metadata.moveSounds.push({ captured: Boolean(result.captured) });
    }

    return metadata;
}

function fitMetadataLength(values, targetLength) {
    const out = Array.isArray(values) ? values.slice(0, targetLength) : [];
    while (out.length < targetLength) {
        out.push(null);
    }
    return out;
}

function countFenPieces(fen) {
    const placement = String(fen || '').trim().split(/\s+/)[0] || '';
    return [...placement].filter(char => /[prnbqk]/i.test(char)).length;
}

/**
 * Build the same minimal descriptor consumed by BoardSounds.playFromMove()
 * directly from authoritative consecutive FENs.  A lower piece count means
 * the transition was a capture (including en passant); otherwise it uses the
 * normal move sound.  This avoids parsing Chess960 SAN or castling rights.
 */
function buildFenListMoveSounds(positions) {
    const sounds = [null];
    for (let index = 1; index < positions.length; index += 1) {
        sounds.push({
            captured: countFenPieces(positions[index]) < countFenPieces(positions[index - 1])
        });
    }
    return sounds;
}

export class FelixChessReplayController {
    constructor(options = {}) {
        this.board = options.board || null;
        this.rootEl = options.rootEl || null;
        this.context = options.context || 'default';
        this.soundController = options.soundController || null;

        this.startFen = '';
        this.moves = [];
        this.clocks = [];      // parallel to moves[]; clocks[i] = remaining time string after ply i+1, or null
        this.initialClocks = null;
        this.positions = [];
        this.moveSounds = [];
        this.currentIndex = 0;

        this.white = null;
        this.black = null;
        this.meta = null;

        this.listeners = new Map();
        trackPositionRendering(this.board, () => this.emit('positionready', this.getState()));
    }

    on(eventName, handler) {
        if (!eventName || typeof handler !== 'function') {
            return () => {};
        }

        if (!this.listeners.has(eventName)) {
            this.listeners.set(eventName, new Set());
        }

        const handlers = this.listeners.get(eventName);
        handlers.add(handler);

        return () => {
            handlers.delete(handler);
            if (handlers.size === 0) {
                this.listeners.delete(eventName);
            }
        };
    }

    emit(eventName, payload = {}) {
        const handlers = this.listeners.get(eventName);
        if (!handlers || handlers.size === 0) {
            return;
        }

        handlers.forEach((handler) => {
            try {
                handler(payload);
            } catch (e) {
                console.error(`FelixChessReplayController: listener failed for "${eventName}"`, e);
            }
        });
    }

    loadGame(options = {}) {
        const positions = Array.isArray(options.positions) ? options.positions : [];
        const isFenListReplay = options.replayMode === 'fenList' || positions.length > 0;
        console.debug('[FelixChess DEBUG BUILD] ReplayController fenList source fix', {
            replayMode: options.replayMode || '',
            positionsLength: positions.length,
            firstFen: positions[0]?.fen || positions[0] || '',
            initialFen: options.initialFen || '',
            variant: options.variant || options.meta?.variant || '',
            gameType: options.gameType || options.meta?.gameType || '',
            isFenListReplay
        });

        const {
            startFen,
            moves,
            clocks,
            initialClocks = null,
            initialPly = 0,
            white = null,
            black = null,
            meta = null
        } = options;
        this.startFen = startFen || '';
        this.moves = Array.isArray(moves) ? moves.slice() : [];
        this.clocks = Array.isArray(clocks) ? clocks.slice() : [];
        this.initialClocks = initialClocks || meta?.initialClocks || null;
        this.positions = [];
        this.lastMoveSquares = [null];   // index 0 = starting position, no last move
        this.moveSounds = [null];        // index n describes the move that reached position n
        this.white = white;
        this.black = black;
        this.meta = meta;

        if (!this.startFen) {
            console.warn('FelixChessReplayController: missing startFen');
            this.clear(false);
            return;
        }

        if (isFenListReplay || isChess960Fen(this.startFen) || positionContext(meta || options).variant === 'chess960') {
            console.debug('[FelixChess][replay] using fenList replay');
            this.positions = positions
                .map((entry) => typeof entry === 'string' ? entry : entry?.fen || entry?.positionFen || '')
                .filter(Boolean);
            if (this.positions.length === 0) {
                this.positions = [this.startFen];
            } else {
                // startFen is the persisted, canonical move-0 position.  Do not
                // prepend it when a stale/incorrect positions[0] is supplied:
                // prepending shifts every ply so Next lands on the old move-0
                // entry instead of positions[1].  Replacing index 0 keeps the
                // server's ply indexing intact and also guarantees that game
                // switches repaint the board from the new game's start FEN.
                this.positions[0] = this.startFen;
            }
            const moveMetadata = positionContext(meta || options).variant === 'standard'
                ? buildMoveMetadata(this.startFen, this.moves) : {lastMoveSquares:[null],moveSounds:[null]};
            this.lastMoveSquares = fitMetadataLength(moveMetadata.lastMoveSquares, this.positions.length);
            // FEN-list replay cannot rely on chess.js for Chess960 metadata,
            // but navigation still uses the shared BoardSounds controller.
            // Supply its normal { captured } transition descriptor from the
            // persisted positions so sound/toggle semantics stay identical.
            this.moveSounds = buildFenListMoveSounds(this.positions);

            const maxIndex = Math.max(0, this.positions.length - 1);
            const requestedIndex = Number.isInteger(initialPly)
                ? initialPly
                : parseInt(initialPly, 10) || 0;

            this.currentIndex = Math.max(0, Math.min(requestedIndex, maxIndex));
            this.applyCurrentPosition(false);

            this.emit('gameloaded', { ...this.getState(), source: 'loadGame' });
            this.emit('positionchange', { ...this.getState(), source: 'loadGame' });
            return;
        }

        const chess = new Chess(this.startFen);
        this.positions.push(chess.fen());

        for (const san of this.moves) {
            const result = chess.move(san, { sloppy: true });
            if (!result) {
                console.warn('FelixChessReplayController: illegal/unparsable move skipped:', san);
                break;
            }
            this.positions.push(chess.fen());
            this.lastMoveSquares.push({ from: result.from, to: result.to });  // ← capture squares
            this.moveSounds.push({ captured: Boolean(result.captured) });
        }

        const maxIndex = Math.max(0, this.positions.length - 1);
        const requestedIndex = Number.isInteger(initialPly)
            ? initialPly
            : parseInt(initialPly, 10) || 0;

        this.currentIndex = Math.max(0, Math.min(requestedIndex, maxIndex));
        this.applyCurrentPosition(false);

        this.emit('gameloaded', { ...this.getState(), source: 'loadGame' });
        this.emit('positionchange', { ...this.getState(), source: 'loadGame' });
    }

    clear(emitEvent = true) {
        this.startFen = '';
        this.moves = [];
        this.clocks = [];
        this.initialClocks = null;
        this.positions = [];
        this.lastMoveSquares = [];
        this.moveSounds = [];
        this.currentIndex = 0;
        this.white = null;
        this.black = null;
        this.meta = null;

        if (emitEvent) {
            this.emit('cleared', {
                ...this.getState(),
                source: 'clear'
            });

            this.emit('positionchange', {
                ...this.getState(),
                source: 'clear'
            });
        }
    }

    applyCurrentPosition(animated = false) {
        // Initial loads call this with animated=false. User navigation passes
        // animated=true so the existing cm-chessboard instance slides pieces
        // between positions instead of flashing or being recreated.
        if (!this.board || !this.positions.length) {
            return;
        }

        const fen = this.positions[this.currentIndex];
        this.board.setPosition(fen, animated);

        // Highlight last move squares
        this.board.removeMarkers(LAST_MOVE_MARKER);

        const squares = this.lastMoveSquares
            ? this.lastMoveSquares[this.currentIndex]
            : null;

        if (squares) {
            this.board.addMarker(LAST_MOVE_MARKER, squares.from); // ← was MARKER_TYPE.lastMove
            this.board.addMarker(LAST_MOVE_MARKER, squares.to);   // ← was MARKER_TYPE.lastMove
        }
    }

    goTo(index, animated = false, source = 'unknown') {
        if (!this.positions.length) {
            return;
        }

        const clamped = Math.max(0, Math.min(index, this.positions.length - 1));
        if (clamped === this.currentIndex) {
            return;
        }

        const previousIndex = this.currentIndex;
        this.currentIndex = clamped;
        this.applyCurrentPosition(animated);
        this.playSoundForTransition(previousIndex, clamped, animated);

        this.emit('positionchange', {
            ...this.getState(),
            source
        });
    }

    first(source = 'unknown', animated = false) {
        this.goTo(0, animated, source);
    }

    prev(source = 'unknown', animated = false) {
        this.goTo(this.currentIndex - 1, animated, source);
    }

    next(source = 'unknown', animated = false) {
        this.goTo(this.currentIndex + 1, animated, source);
    }

    last(source = 'unknown', animated = false) {
        this.goTo(this.positions.length - 1, animated, source);
    }

    setBoard(board) {
        this.board = board || null;
    }

    playSoundForTransition(fromIndex, toIndex, animated) {
        if (!animated || !this.soundController || fromIndex === toIndex) {
            return;
        }
        const soundIndex = toIndex > fromIndex ? toIndex : fromIndex;
        this.soundController.playFromMove(this.moveSounds?.[soundIndex]);
    }

    getState() {
        const totalPlies = Math.max(0, this.positions.length - 1);

        return {
            rootEl: this.rootEl,
            context: this.context,

            startFen: this.startFen,
            moves: this.moves.slice(),
            clocks: this.clocks.slice(),
            initialClocks: this.initialClocks,
            positions: this.positions.slice(),
            currentIndex: this.currentIndex,
            currentFen: this.positions[this.currentIndex] || this.startFen || '',

            totalPlies,
            totalMoves: totalPlies > 0 ? Math.ceil(totalPlies / 2) : 0,
            currentMove: totalPlies > 0 ? Math.max(1, Math.ceil(this.currentIndex / 2)) : 0,

            white: this.white,
            black: this.black,
            meta: this.meta,

            hasGame: !!this.startFen && this.positions.length > 0,
            atStart: this.currentIndex <= 0,
            atEnd: this.positions.length === 0 || this.currentIndex >= this.positions.length - 1
        };
    }

    destroy() {
        this.listeners.clear();
        this.board = null;
        this.rootEl = null;
    }
}
